import React, { useEffect, memo, useState, useRef } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import Common from 'us.common';
import { workflowAction } from 'us.collection/actions';
import {
	BoxIcons,
	IconTypes,
	PlusOutlined,
	UploadOutlined,
	SearchOutlined
} from 'us.icons';
import { NewState, StateCategory, WorkflowCategory, Event, ImportWorkflow } from './Components';
import {
	StateType,
	workflowDrawerInitial,
	IMPORT_DRAWER_INITIAL,
	WorkflowDrawerType,
	WorkflowView,
	WorkflowStateStatus,
	WorkflowSequenceType,
	WorkflowSequenceCategoryId,
	WorkflowViewType
} from './Constants';
import { matchPath, useNavigate, useLocation } from 'react-router-dom';
import { getEntityNumberAndType } from '../BMD/Functions';
import { handleGoBack } from 'us.helper/utility';
import { WorkflowGroup } from 'us.collection/repository';
import { IWorkflowGroup } from 'us.collection/reducers/Workflow/Interfaces';
import { URLType, EntityType } from 'us.collection/constants';
import {
	searchState,
	getTroubleshootNavigationUrl,
	getOverriddenLevels,
	updateSequence
} from './Functions';
import { RootState } from 'us.helper/types';
import { useDndScrolling } from 'react-dnd-scrolling';
import { SaveSequence } from "./Repository";
import './Home.scss';

const {
	$Button,
	$Divider,
	$PageHeader,
	$Drawer,
	$Affix,
	$Skeleton,
	$Radio,
	$Tooltip,
	$Select,
	$Input,
	$InputGroup,
	$Checkbox,
} = Common.Components;

/**
 * @description - The component used in home view of Workflow
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3055714493/Manage+State+Categories
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 14/11/2022
 */

const Workflow: React.FC<PropsFromRedux> = memo((props) => {
	const {
		importDrawer,
		workflowStates,
		exportWorkflows,
		getWorkflowStates,
		openImportDrawer,
		workflowDrawer,
		openDrawer,
		saveSequence,
		metaData,
		searchFilters,
	} = props;

	const { t } = useTranslation();

	const location = useLocation();
	const navigate = useNavigate();
	const pathname = location.pathname;
	const mainScrollRef = useRef<any>(null);
	const optionalScrollRef = useRef<any>(null);

	useDndScrolling(mainScrollRef, {});
	useDndScrolling(optionalScrollRef, {});

	const { params }: any = matchPath( {
		path: '/:type/:id?/:module?',
		end: false,
		caseSensitive: false,
	},pathname);

	const { caseId } = metaData.data ?? {};

	const [visibleTab, setVisibleTab] = useState(WorkflowView.STATES);

	useEffect(() => {
		getWorkflowStates &&
			getWorkflowStates(
				WorkflowGroup.call(
					getEntityNumberAndType(
						params['type'],
						params['id'],
						caseId?.toString() ?? ''
					)
				)
			);
	}, []);

	/**
	 * @description Navigation click action for troubleshoot button
	 */
	const handleTroubleshootNavigation = () => {
		window.scrollTo(0, 0);
		navigate(getTroubleshootNavigationUrl(
			params['id'],
			params['type']
		));
	};

	/**
	 * @description Handles overridden tags click action
	 * @param values formik valus
	 * @param rest formik rest props
	 * @param overriddenLevel selected overridden level
	 */
	const handleOverriddenSelection = (
		values: any,
		rest: any,
		overriddenLevel: string
	) => {
		const { overriddenBy } = values ?? {};
		const { setFieldValue } = rest ?? {};

		if (overriddenBy && setFieldValue) {
			if (overriddenBy?.includes(overriddenLevel)) {
				setFieldValue(
					'overriddenBy',
					getOverriddenLevels(
						overriddenBy,
						overriddenLevel
					)
				);
			} else {
				setFieldValue('overriddenBy', [
					...overriddenBy,
					overriddenLevel,
				]);
			}
		}
	};

	/**
	 * @description Update sequence of categories
	 * @param fromIndex source index
	 * @param toIndex target index
	 */
	const updateCategorySequence = (fromIndex: number, toIndex: number) => {
		const sequenceList = updateSequence(
				fromIndex,
				toIndex,
				workflowStates.data,
				WorkflowViewType.OTHER
			);
		const saveParams = SaveSequence.call({
				type: WorkflowSequenceType.CATEGORY,
				categoryId: WorkflowSequenceCategoryId.CATEGORY,
				sequenceList,
			});
		saveSequence && saveSequence({ sequenceList, saveParams });
 	};

	/**
	 * @description Export workflow files
	 * @param selectedWorkflows selected files
	 */
	const exportWorkflowFiles = (selectedWorkflows:Array<any>) => {
		exportWorkflows && exportWorkflows(selectedWorkflows);
	}

	return (
		<Formik
			enableReinitialize
			initialValues={{
				...searchFilters,
				statesAndEvent: visibleTab,
				isDraggable: false,
				selectedWorkflows: [],
				selectionEnabledCategory: null
			}}
			onSubmit={() => {}}>
			{({
				values,
				handleChange,
				handleBlur,
				handleSubmit,
				isSubmitting,
				isValidating,
				setFieldValue,
				setValues,
				...rest
			}: any) => (
				<>
					<div className='workflows'>
						<$Affix offsetTop={80}>
							<div className='page-header header-border'>
								<div className='d-flex flex-row align-items-center justify-content-between'>
									<div className='d-flex align-items-center'>
										<$PageHeader
											className='px-0'
											title={t(
												'US.COLLECTION.CASE:WORKFLOWS.WORKFLOWS'
											)}
											onBack={() =>
												handleGoBack(
													navigate
												)
											}
										/>
										<div className='states-events-toggle'>
											<$Radio
												name='statesAndEvent'
												options={[
													{
														value: WorkflowView.STATES,
														label: t(
															'US.COLLECTION.CASE:WORKFLOWS.STATES'
														),
													},
													{
														value: WorkflowView.EVENTS,
														label: t(
															'US.COLLECTION.CASE:WORKFLOWS.EVENTS'
														),
													},
												]}
												onChange={(
													e: any
												) => {
													setVisibleTab(
														e
															.target
															.value
													);
												}}
												className='d-flex'
												optionStyle='flex-fill text-center'
												optionType='button'
												buttonStyle='solid'
												radioButton='true'
												optionValue='value'
												optionContent={(
													item: any,
													index: number
												) =>
													item.label
												}
												size='small'
											/>
										</div>
										<$Button
											name='troubleshoot'
											size='small'
											className='ml-5'
											onClick={() =>
												handleTroubleshootNavigation()
											}>
											<BoxIcons
												type={
													IconTypes.BOX_ICON
												}
												name='workflows-troubleshoot'
												className='mr-1'
											/>
											{t(
												'US.COLLECTION.CASE:WORKFLOWS.TROUBLESHOOT'
											)}
										</$Button>
										{params["type"] == URLType.BUREAU && (
										<$Button
											name='import'
											size='small'
											className='ml-3'
											data-testid="import-workflow"
											onClick={() =>
												openImportDrawer &&
												openImportDrawer(
													{
														...IMPORT_DRAWER_INITIAL,
														title: visibleTab == WorkflowView.STATES ? 'US.COLLECTION.CASE:WORKFLOWS.IMPORT_WORKFLOW_STATES' : 'US.COLLECTION.CASE:WORKFLOWS.IMPORT_WORKFLOW_EVENTS',
														isVisible: true,
														workflowView: visibleTab
													}
												)
											}>
											{t(
												'US.COLLECTION.CASE:WORKFLOWS.IMPORT_WORKFLOW'
											)}
										</$Button>
										)}
										{params["type"] == URLType.BUREAU && (
										<div className="d-flex align-items-center ml-3">
										  <$Tooltip
											placement="top"
											title={
												(values?.searchText ||
												values?.activeStates != WorkflowStateStatus.ALL || 
												values?.activeEvents != WorkflowStateStatus.ALL)
												  ? t(
													  "US.COLLECTION.CASE:WORKFLOWS.CLEAR_ALL_FILTERS_FOR_ENABLE_REORDERING"
													)
												  : values?.isDraggable
												  ? t(
													  "US.COLLECTION.CASE:WORKFLOWS.DISABLE_REORDER"
													)
												  : t("US.COLLECTION.CASE:WORKFLOWS.ENABLE_REORDER")
											  }>
											<$Button
												type={values?.isDraggable ? "primary" : "ghost"}
												data-testid="enable-drag"
												size="small"
												icon={<BoxIcons
													type={
														IconTypes.BOX_ICON
													}
													name='drag'
													viewBox='-2 1 67 65'
												/>}
												disabled={
													values?.searchText ||
													values?.activeStates != WorkflowStateStatus.ALL ||
													values?.activeEvents != WorkflowStateStatus.ALL
												}
												onClick={() =>
												setValues((previousValues: any) => 
													({...previousValues, selectedWorkflows: [], 
														selectionEnabledCategory: null, isDraggable: !values?.isDraggable  }))
												}
											/>
										  </$Tooltip>
										</div>
										)}
									</div>

									<div className='d-flex align-items-center flex-grow-1 justify-content-end'>
										
										
										{visibleTab ==
											WorkflowView.STATES &&
											params[
												'type'
											] !=
												URLType.BUREAU && (
												<div className='d-flex align-items-center mr-4'>
													<$Tooltip
														placement='top'
														title={t(
															'US.COLLECTION.CASE:WORKFLOWS.OVERRIDDEN_IN_BUREAU'
														)}>
														<span>
															<$Checkbox
																name='bu'
																checked={values.overriddenBy?.includes(
																	''
																)}
																className='ml-1 mr-0 checkbox-bu'
																onChange={() =>
																	handleOverriddenSelection(
																		values,
																		rest,
																		''
																	)
																}
															/>
														</span>
													</$Tooltip>
													<$Tooltip
														placement='top'
														title={t(
															'US.COLLECTION.CASE:WORKFLOWS.OVERRIDDEN_IN_CREDITOR_GROUP'
														)}>
														<span>
															<$Checkbox
																name='cg'
																checked={values.overriddenBy?.includes(
																	EntityType.CREDITOR_GROUP
																)}
																className='ml-1 mr-0 checkbox-cg'
																onChange={() =>
																	handleOverriddenSelection(
																		values,
																		rest,
																		EntityType.CREDITOR_GROUP
																	)
																}
															/>
														</span>
													</$Tooltip>
													<$Tooltip
														placement='top'
														title={t(
															'US.COLLECTION.CASE:WORKFLOWS.OVERRIDDEN_IN_CREDITOR'
														)}>
														<span>
															<$Checkbox
																name='cr'
																checked={values.overriddenBy?.includes(
																	EntityType.CREDITOR
																)}
																className='ml-1 mr-0 checkbox-cr'
																onChange={() =>
																	handleOverriddenSelection(
																		values,
																		rest,
																		EntityType.CREDITOR
																	)
																}
															/>
														</span>
													</$Tooltip>
												</div>
											)}
										<div className='d-flex flex-1 align-items-center mr-2'>
											<div
												onClick={(e)=> e.stopPropagation()}
												style={{
													lineHeight: 1,
												}}>
												<$InputGroup
													compact
													className='no-mb-group-item mt-1'>
													<$Tooltip
														title={t(
															'COMMON.STATUS'
														)}
														placement='top'>
														{visibleTab ==
															WorkflowView.STATES && (
															<$Select
																name='activeStates'
																size='small'
																options={[
																	{
																		label: t(
																			'COMMON.ACTIVE'
																		),
																		value: WorkflowStateStatus.ACTIVE,
																	},
																	{
																		label: t(
																			'COMMON.INACTIVE'
																		),
																		value: WorkflowStateStatus.INACTIVE,
																	},
																]}
																defaultValue={
																	WorkflowStateStatus.ALL
																}
																optionText='label'
																optionValue='value'
																hideSearch={
																	true
																}
																style={{
																	width: '120px',
																}}
																className='mt-1'
															/>
														)}
														{visibleTab ==
															WorkflowView.EVENTS && (
															<$Select
																name='activeEvents'
																size='small'
																options={[
																	{
																		label: t(
																			'US.COLLECTION.CASE:WORKFLOWS.EXECUTABLE_FROM_DOMAIN_VIEW'
																		),
																		value: WorkflowStateStatus.ACTIVE,
																	},
																	{
																		label: t(
																			'US.COLLECTION.CASE:WORKFLOWS.NOT_EXECUTABLE_FROM_DOMAIN_VIEW'
																		),
																		value: WorkflowStateStatus.INACTIVE,
																	},
																]}
																defaultValue={
																	WorkflowStateStatus.ALL
																}
																optionText='label'
																optionValue='value'
																hideSearch={
																	true
																}
																style={{
																	width: '235px',
																}}
																className='mt-1'
															/>
														)}
													</$Tooltip>
													<$Input
														size='small'
														name='searchText'
														className='mb-0'
														style={{
																	width: '180px',
																}}
														allowClear={
															true
														}
														value={
															values.searchText
														}
														placeholder={t(
															'US.COLLECTION.CASE:WORKFLOWS.SEARCH_STATES_EVENTS'
														)}
														addonAfter={
															<a className='px-2'>
																<SearchOutlined />
															</a>
														}
													/>
												</$InputGroup>
											</div>
										</div>
									</div>

									{false && ( // This use in next feature implementation
										<$Button
											type='dashed'
											size='small'
											icon={
												<UploadOutlined />
											}
											disabled={
												true
											}>
											{t(
												'US.COLLECTION.CASE.COMMON:COMMON.UPLOAD'
											)}
										</$Button>
									)}
								</div>
							</div>
						</$Affix>
						<$Skeleton
							loading={
								workflowStates?.isLoading &&
								visibleTab ==
									WorkflowView.STATES
							}
							active
							paragraph={{ rows: 2 }}
							className={
								workflowStates?.isLoading
									? 'p-3'
									: ''
							}>
							{visibleTab ===
								WorkflowView.STATES && (
								<div className='workflows-states-layout'>
									<div className='workflows-states-layout-col'>
										<div className='d-flex align-items-center'>
											<h3 className='mb-0'>
												{t(
													'US.COLLECTION.CASE:WORKFLOWS.MAIN_STATES'
												)}
											</h3>
											<$Divider
												className='bui-devider'
												type='vertical'
											/>
											<$Button
												type='default'
												size='small'
												icon={
													<PlusOutlined />
												}
												onClick={() => {
													openDrawer &&
														openDrawer(
															{
																...workflowDrawerInitial,
																title: 'US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_MAIN_STATE_CATEGORY',
																isVisible: true,
																type: WorkflowDrawerType.CATEGORY,
																stateType: StateType.MAIN,
															}
														);
												}}>
												{t(
													'US.COLLECTION.CASE:WORKFLOWS.NEW_CATEGORY'
												)}
											</$Button>
										</div>
										<div ref={mainScrollRef} className='ws-cat-wrap mt-4'>
											<div className='pr-3'>
												<div className='ws-tile-container'>
													{searchState(
														workflowStates?.data,
														values
													) &&
														searchState(
															workflowStates?.data,
															values
														)?.map(
															(
																group: IWorkflowGroup,
																index: number
															) => {
																if (
																	group?.type ===
																	StateType.MAIN
																)
																	return (
																		<StateCategory
																			index={index}
																			key={
																				group?.stateCategoryId
																			}
																			workflowGroup={
																				group
																			}
																			stateType={
																				StateType.MAIN
																			}
																			onMove={updateCategorySequence}
																			exportWorkflowFiles={()=> {
																				exportWorkflowFiles(values?.selectedWorkflows)
																				setValues((previousValues: any) => 
																					({...previousValues, selectedWorkflows: [], selectionEnabledCategory: null }))
																			}}
																		/>
																	);
															}
														)}
												</div>
											</div>
										</div>
									</div>
									<div className='workflows-states-layout-col'>
										<div className='d-flex align-items-center'>
											<h3 className='mb-0'>
												{t(
													'US.COLLECTION.CASE:WORKFLOWS.OPTIONAL_STATES'
												)}
											</h3>
											<$Divider
												className='bui-devider'
												type='vertical'
											/>
											<$Button
												type='default'
												size='small'
												icon={
													<PlusOutlined />
												}
												onClick={() => {
													openDrawer &&
														openDrawer(
															{
																...workflowDrawerInitial,
																title: 'US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_OPTIONAL_STATE_CATEGORY',
																isVisible: true,
																type: WorkflowDrawerType.CATEGORY,
																stateType: StateType.OPTIONAL,
															}
														);
												}}>
												{t(
													'US.COLLECTION.CASE:WORKFLOWS.NEW_CATEGORY'
												)}
											</$Button>
										</div>
										<div ref={optionalScrollRef} className='ws-cat-wrap mt-4'>
											<div className='pr-3'>
												<div className='ws-tile-container'>
													{searchState(
														workflowStates?.data,
														values
													) &&
														searchState(
															workflowStates?.data,
															values
														)?.map(
															(
																group: IWorkflowGroup,
																index: number
															) => {
																if (
																	group?.type ===
																	StateType.OPTIONAL
																)
																	return (
																		<StateCategory
																			index={index}
																			key={
																				group?.stateCategoryId
																			}
																			workflowGroup={
																				group
																			}
																			stateType={
																				StateType.OPTIONAL
																			}
																			onMove={updateCategorySequence}
																			exportWorkflowFiles={()=> {
																				exportWorkflowFiles(values?.selectedWorkflows)
																				setValues((previousValues: any) => 
																					({...previousValues, selectedWorkflows: [], selectionEnabledCategory: null }))
																			}}
																		/>
																	);
															}
														)}
												</div>
											</div>
										</div>
									</div>
								</div>
							)}
						</$Skeleton>
						{visibleTab ===
							WorkflowView.EVENTS && (
							<Event exportWorkflowFiles={()=> {
								exportWorkflowFiles(values?.selectedWorkflows)
								setValues((previousValues: any) => 
									({...previousValues, selectedWorkflows: [], selectionEnabledCategory: null }))
								}} 
							/>
						)}
					</div>
					<$Drawer
						title={t(workflowDrawer.title)}
						width={600}
						open={
							workflowDrawer.isVisible
						}
						onClose={() =>
							openDrawer &&
							openDrawer(
								workflowDrawerInitial
							)
						}
						destroyOnClose>
						{workflowDrawer?.type ===
							WorkflowDrawerType.CATEGORY && (
							<WorkflowCategory
								onClose={() =>
									openDrawer &&
									openDrawer(
										workflowDrawerInitial
									)
								}
							/>
						)}
						{workflowDrawer?.type ===
							WorkflowDrawerType.STATE && (
							<NewState
								onClose={() =>
									openDrawer &&
									openDrawer(
										workflowDrawerInitial
									)
								}
							/>
						)}
					</$Drawer>
					<$Drawer
						title={t(importDrawer.title)}
						width={800}
						open={importDrawer.isVisible}
						onClose={() => openImportDrawer && openImportDrawer(IMPORT_DRAWER_INITIAL)}
						destroyOnClose
						maskClosable={false}
					>
						<ImportWorkflow
						onClose={() => openImportDrawer && openImportDrawer(IMPORT_DRAWER_INITIAL)}
						/>
					</$Drawer>
				</>
			)}
		</Formik>
	);
});

const { workflowStates, category, workflowState, sequence, importWorkflow, exportWorkflow } = workflowAction;

const mapStateToProps = (state: RootState) => {
	const { Workflow, domainView } = state;
	const { workflowStates, workflowDrawer, searchFilters, importDrawer } = Workflow;
	const { metaData } = domainView;
	return {
		workflowStates,
		workflowDrawer,
		metaData,
		searchFilters,
		importDrawer
	};
};

const mapDispatchToProps = {
	getWorkflowStates: workflowStates.get,
	openDrawer: category.openDrawer,
	openStateDrawer: workflowState.openDrawer,
	changeSequence: sequence.update,
	saveSequence: sequence.save,
	exportWorkflows: exportWorkflow.save,
	openImportDrawer: importWorkflow.openDrawer
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Workflow);
